import { type ElementRef, useEffect, useRef, useState } from "react"

interface VoiceRecorderProps {
  onPermissionError: () => void
  onRecordingStart: () => void
  onRecordingStop: (url: string) => void
  startExternal: boolean
  setFieldValue: (field: string, value: unknown) => void
}

export default function VoiceRecorder({
  onPermissionError,
  onRecordingStart,
  onRecordingStop,
  startExternal,
  setFieldValue,
}: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false)
  const [permissionGranted, setPermissionGranted] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder>()
  const audioChunks = useRef<Array<Blob>>([])
  const audioContextRef = useRef<AudioContext>()
  const analyserRef = useRef<AnalyserNode>()
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer>>()
  const animationFrameIdRef = useRef<number>()
  const streamRef = useRef<MediaStream>()
  const canvasRef = useRef<ElementRef<"canvas">>(null)

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks()
        tracks.forEach((track) => track.stop())
      }
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current)
      }
    }
  }, [])

  const requestPermissionAndStart = async () => {
    try {
      if (!permissionGranted) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        })
        streamRef.current = stream
        setPermissionGranted(true)
        mediaRecorderRef.current = new MediaRecorder(stream)

        audioContextRef.current = new AudioContext()
        const source = audioContextRef.current.createMediaStreamSource(stream)
        analyserRef.current = audioContextRef.current.createAnalyser()
        analyserRef.current.fftSize = 256
        source.connect(analyserRef.current)

        dataArrayRef.current = new Uint8Array(
          analyserRef.current.frequencyBinCount
        )

        mediaRecorderRef.current.onstart = () => {
          drawWaveform()
          onRecordingStart()
        }

        mediaRecorderRef.current.ondataavailable = (event) => {
          audioChunks.current.push(event.data)
        }

        mediaRecorderRef.current.onstop = () => {
          const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" })
          const url = URL.createObjectURL(audioBlob)
          audioChunks.current = []
          if (animationFrameIdRef.current) {
            cancelAnimationFrame(animationFrameIdRef.current)
          }
          setFieldValue("content_file", audioBlob)
          setFieldValue("type", "1")
          onRecordingStop(url)

          setIsRecording(false)
        }
      }
      startRecording()
    } catch {
      onPermissionError()
    }
  }

  const drawWaveform = () => {
    if (isRecording && analyserRef.current && dataArrayRef.current) {
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext("2d") as CanvasRenderingContext2D

      // Handle canvas scaling for high-DPI screens
      const dpr = window.devicePixelRatio || 1
      const styleWidth = canvas.offsetWidth
      const styleHeight = canvas.offsetHeight

      canvas.width = styleWidth * dpr
      canvas.height = styleHeight * dpr

      ctx.scale(dpr, dpr)

      const width = canvas.offsetWidth // Logical width
      const height = canvas.offsetHeight // Logical height
      const centerX = width / 2 // Center position

      analyserRef.current.getByteFrequencyData(dataArrayRef.current)
      ctx.clearRect(0, 0, width, height)

      const barWidth = 6 // Fixed bar width
      const gap = 3 // Gap between bars

      const numBars = Math.floor(width / (barWidth + gap))
      const barHeights = dataArrayRef.current.slice(0, numBars)
      const centerY = height / 2

      for (let i = 0; i < numBars; i++) {
        let barHeight = (barHeights[i] / 255) * height
        barHeight = Math.max(barHeight, 10) // Ensure minimum height

        const offset = i * (barWidth + gap)

        ctx.fillStyle = "#243B78"

        ctx.beginPath()
        ctx.roundRect(
          centerX + offset,
          centerY - barHeight / 2,
          barWidth,
          barHeight,
          barWidth / 2
        )
        ctx.roundRect(
          centerX - offset,
          centerY - barHeight / 2,
          barWidth,
          barHeight,
          barWidth / 2
        )
        ctx.fill()
        ctx.closePath()
      }

      animationFrameIdRef.current = requestAnimationFrame(drawWaveform)
    }
  }

  const startRecording = () => {
    if (mediaRecorderRef.current) {
      audioChunks.current = []
      mediaRecorderRef.current.start()
      setIsRecording(true)
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" })
        const url = URL.createObjectURL(audioBlob)

        audioChunks.current = [] // Clear chunks

        // Call setFieldValue with the audioBlob
        setFieldValue("content_file", audioBlob)

        if (animationFrameIdRef.current) {
          cancelAnimationFrame(animationFrameIdRef.current)
        }
        onRecordingStop(url)

        setIsRecording(false)
      }
    }
  }

  useEffect(() => {
    drawWaveform()
  }, [isRecording])
  useEffect(() => {
    if (startExternal) {
      if (permissionGranted) {
        startRecording()
      } else {
        requestPermissionAndStart()
      }
    } else if (!startExternal && isRecording) {
      stopRecording()
    }
  }, [startExternal])

  return (
    <div style={{ flexGrow: "1" }}>
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          flexGrow: 1,
          height: "34px",
          display: "block",
        }}
      ></canvas>
    </div>
  )
}
